// Admin > Features (2026-11-05): big UI changes ship behind a switch. Each flag: Off / Just me (admins) / Everyone,
// saved the moment it's tapped. GET /api/admin/features, POST /api/admin/features/:key {state}. Code reads them with
// useFlag / flagOn (src/flags.ts) from GET /api/me, which is reloaded after a change so this admin sees it at once.
import { useState } from "react";
import { Empty, ErrorNote, Spinner, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { FLAG_STATES, listFeatures, setFeature, type Feature, type FlagState } from "../pipeline";
import { Link } from "../router";
import { bump, toast, useStore } from "../store";

export function AdminFeaturesScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const { data, setData, error, loading, reload } = useLoad(() => (admin ? listFeatures() : Promise.resolve([] as Feature[])), [admin]);
  const [saving, setSaving] = useState<string | null>(null);

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            Features are switched by the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  const set = async (f: Feature, state: FlagState) => {
    if (f.state === state) return;
    const before = data ?? [];
    setData(before.map((x) => (x.key === f.key ? { ...x, state } : x)));
    setSaving(f.key);
    try {
      await setFeature(f.key, state);
      bump("me"); // this admin's own flags
    } catch (e) {
      setData(before);
      toast(`Couldn't save that. ${(e as Error).message}`, "bad");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Shell>
      <div className="page">
        <header className="page-head">
          <h1 className="page-title">Features</h1>
          <p className="page-lede">New things that are switched on bit by bit. Just me: only admins see it.</p>
        </header>
        {error && <ErrorNote text={`Couldn't load the features. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading" />}
        {data && !data.length && <p className="muted">No features yet.</p>}
        {!!data?.length && (
          <ul className="fb-rows">
            {data.map((f) => (
              <li key={f.key} className="fb-row">
                <p className="fb-text">
                  <strong>{f.title}</strong>
                </p>
                {f.description && <p className="fb-meta">{f.description}</p>}
                <div className="seg" role="radiogroup" aria-label={`${f.title}: who sees it`}>
                  {FLAG_STATES.map((s) => (
                    <button key={s.id} type="button" role="radio" aria-checked={f.state === s.id} disabled={saving === f.key} onClick={() => void set(f, s.id)}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Shell>
  );
}
